(() => {
  if (customElements.get('bigfoot-bonus')) return;

  class BigfootBonus extends HTMLElement {
    constructor() {
      super();
      this.isSubmitting = false;
      this.handleClick = this.handleClick.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.status = this.querySelector('[data-bigfoot-status]');
      this.cartFallback = this.querySelector('[data-bigfoot-cart-link]');
      this.chooseButtons = Array.from(this.querySelectorAll('[data-bigfoot-choose]'));

      this.chooseButtons.forEach((button) => {
        const label = button.querySelector('[data-bigfoot-button-label]');
        button.dataset.originalLabel = label?.textContent?.trim() || button.textContent.trim();
      });

      this.addEventListener('click', this.handleClick);
      window.addEventListener('pageshow', this.handlePageShow);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      window.removeEventListener('pageshow', this.handlePageShow);
      this.initialized = false;
    }

    handlePageShow() {
      this.resetSubmissionState();
      this.setStatus('');

      if (this.cartFallback) {
        this.cartFallback.hidden = true;
        this.cartFallback.removeAttribute('href');
      }
    }

    handleClick(event) {
      const button = event.target.closest('[data-bigfoot-choose]');
      if (!button || !this.contains(button) || this.isSubmitting) return;

      this.addPrize(button);
    }

    async addPrize(button) {
      const variantId = Number(button.dataset.variantId);
      const discountCode = (this.dataset.discountCode || '').trim();

      if (!Number.isSafeInteger(variantId) || variantId <= 0) {
        this.showError(this.dataset.errorMessage || 'This prize could not be selected. Please try again.');
        return;
      }

      if (!discountCode) {
        this.showError('The Bigfoot offer is not configured yet. Please keep shopping and contact us for help.');
        return;
      }

      this.isSubmitting = true;
      this.setAttribute('aria-busy', 'true');
      this.setStatus(this.dataset.loadingMessage || 'Adding your prize…');

      this.chooseButtons.forEach((chooseButton) => {
        chooseButton.disabled = true;
        chooseButton.setAttribute('aria-disabled', 'true');
      });

      const activeLabel = button.querySelector('[data-bigfoot-button-label]');
      if (activeLabel) activeLabel.textContent = this.dataset.loadingMessage || 'Adding your prize…';

      const discountUrl = this.discountUrl(discountCode);
      let cartResponse;
      let cartData = {};

      try {
        cartResponse = await fetch(this.cartUrl(), {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        cartData = await cartResponse.json().catch(() => ({}));
      } catch (error) {
        this.resetSubmissionState();
        this.showError(this.dataset.errorMessage || 'Nothing was added. Please try again.');
        return;
      }

      if (!cartResponse.ok || !Array.isArray(cartData.items)) {
        this.resetSubmissionState();
        this.showError(this.dataset.errorMessage || 'Nothing was added. Please try again.');
        return;
      }

      const existingBonus = cartData.items.some((item) => {
        return item.properties?._bigfoot_bonus === discountCode;
      });

      if (existingBonus) {
        this.setStatus(
          this.dataset.existingMessage || 'Your Bigfoot bonus is already in the cart. Opening it now…',
          'success'
        );
        this.openDiscountCart(discountUrl);
        return;
      }

      let response;
      let responseData = {};

      try {
        response = await fetch(this.cartAddUrl(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: {
                  _bigfoot_bonus: discountCode
                }
              }
            ]
          })
        });

        responseData = await response.json().catch(() => ({}));
      } catch (error) {
        this.resetSubmissionState();
        this.showError(this.dataset.errorMessage || 'Nothing was added. Please try again.');
        return;
      }

      if (!response.ok) {
        const shopifyMessage = [responseData.description, responseData.message].find((message) => {
          return typeof message === 'string' && message.trim();
        });
        this.resetSubmissionState();
        this.showError(shopifyMessage || this.dataset.errorMessage || 'Nothing was added. Please try again.');
        return;
      }

      this.setStatus(
        this.dataset.successMessage || 'Prize added. Opening your cart with the Bigfoot offer…',
        'success'
      );

      this.openDiscountCart(discountUrl);
    }

    openDiscountCart(discountUrl) {
      if (this.cartFallback) {
        this.cartFallback.href = discountUrl;
      }

      try {
        window.location.assign(discountUrl);
      } catch (error) {
        this.setStatus('Your prize was added. Use the button below to open your cart with the offer.', 'success');
        if (this.cartFallback) this.cartFallback.hidden = false;
      }
    }

    cartAddUrl() {
      if (this.dataset.cartAddUrl) return this.dataset.cartAddUrl;

      return `${this.shopifyRoot()}cart/add.js`;
    }

    cartUrl() {
      if (this.dataset.cartUrl) return this.dataset.cartUrl;

      return `${this.shopifyRoot()}cart.js`;
    }

    discountUrl(discountCode) {
      const root = this.shopifyRoot();
      const cartPath = `${root}cart`;

      return `${root}discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(cartPath)}`;
    }

    shopifyRoot() {
      const routeRoot = window.Shopify?.routes?.root || '/';
      return routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
    }

    resetSubmissionState() {
      this.isSubmitting = false;
      this.setAttribute('aria-busy', 'false');

      this.chooseButtons?.forEach((button) => {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        const label = button.querySelector('[data-bigfoot-button-label]');
        if (label && button.dataset.originalLabel) label.textContent = button.dataset.originalLabel;
      });
    }

    showError(message) {
      this.setStatus(message, 'error');
    }

    setStatus(message, state = '') {
      if (!this.status) return;

      this.status.textContent = message;
      this.status.classList.toggle('is-error', state === 'error');
      this.status.classList.toggle('is-success', state === 'success');
    }
  }

  customElements.define('bigfoot-bonus', BigfootBonus);
})();
